import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getMe, globalSync } from './api';
import axios from 'axios';
import { normalizeModule, normalizeModuleList, hasModuleAccess } from './normalizeModule';
import logger from './logger';

const AuthContext = createContext(null);

// Constants
const TOKEN_KEY = 'cr_access_token';
const CONTROL_ROOM_URL = (process.env.REACT_APP_CONTROL_ROOM_URL || '').replace(/\/+$/, '');
const APP_CODE = 'INVENTORY';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Helper to get token
export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);

/**
 * RBAC Helper: Check if user can access the INVENTORY app
 * Super admin and org admin have override access regardless of allowed_apps
 * Uses normalized module codes for consistent matching
 */
export function canAccessApp(me, appCode = APP_CODE) {
  if (!me) {
    logger.info('[RBAC] canAccessApp: no user object');
    return false;
  }
  
  const normalizedRequired = normalizeModule(appCode);
  
  // Log all relevant RBAC fields for debugging
  logger.info('[RBAC] Checking access for:', me.email);
  logger.info('[RBAC] Required module (normalized):', normalizedRequired);
  logger.info('[RBAC] is_super_admin:', me.is_super_admin);
  logger.info('[RBAC] is_org_admin:', me.is_org_admin);
  logger.info('[RBAC] role:', me.role);
  logger.info('[RBAC] platform_role_code:', me.platform_role_code);
  logger.info('[RBAC] allowed_apps (raw):', me.allowed_apps);
  logger.info('[RBAC] app_access (raw):', me.app_access);
  logger.info('[RBAC] module_codes (raw):', me.module_codes);
  logger.info('[RBAC] roles:', me.roles);
  
  // Super admin override - check multiple possible fields
  if (me.is_super_admin === true) {
    logger.info('[RBAC] Access GRANTED: is_super_admin=true (SUPER ADMIN BYPASS)');
    return true;
  }
  
  // Check role field for SUPER_ADMIN
  if (me.role === 'SUPER_ADMIN' || me.platform_role_code === 'SUPER_ADMIN') {
    logger.info('[RBAC] Access GRANTED: role/platform_role_code is SUPER_ADMIN');
    return true;
  }
  
  // Check roles array
  if (me.roles?.includes('SUPER_ADMIN')) {
    logger.info('[RBAC] Access GRANTED: roles includes SUPER_ADMIN');
    return true;
  }
  
  // Org admin override
  if (me.is_org_admin === true) {
    logger.info('[RBAC] Access GRANTED: is_org_admin=true');
    return true;
  }
  
  // Normalize all app/module lists for comparison
  const allowedApps = normalizeModuleList(me.allowed_apps);
  const appAccess = normalizeModuleList(me.app_access);
  const moduleCodes = normalizeModuleList(me.module_codes);
  
  logger.info('[RBAC] allowed_apps (normalized):', allowedApps);
  logger.info('[RBAC] app_access (normalized):', appAccess);
  logger.info('[RBAC] module_codes (normalized):', moduleCodes);
  
  // Combine all for checking
  const allModules = [...allowedApps, ...appAccess, ...moduleCodes];
  
  // Check for wildcard
  if (allModules.includes('*')) {
    logger.info('[RBAC] Access GRANTED: wildcard * access found');
    return true;
  }
  
  // Check if required module is in any list
  if (allModules.includes(normalizedRequired)) {
    logger.info('[RBAC] Access GRANTED:', normalizedRequired, 'found in allowed modules');
    return true;
  }
  
  logger.info('[RBAC] Access DENIED for', me.email, '- no matching access rule for', normalizedRequired);
  return false;
}

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    token: null,
    user: null,
    allowedApps: [],
    isAuthenticated: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Restore session on app load
  const restoreSession = useCallback(async () => {
    logger.info('[Auth] App root - window.location.origin:', window.location.origin);
    
    const token = localStorage.getItem(TOKEN_KEY);
    logger.info('[Auth] App load - checking for existing token:', token ? 'FOUND' : 'NOT FOUND');
    
    if (!token) {
      logger.info('[Auth] No token, showing login');
      setLoading(false);
      return false;
    }

    try {
      logger.info('[Auth] Token exists, calling /api/inventory/context...');
      const meData = await getMe();
      logger.info('[Auth] /api/inventory/context status: 200 OK');
      logger.info('[Auth] /api/inventory/context full response:', JSON.stringify(meData, null, 2));
      
      // Use RBAC helper for access check
      const hasAccess = canAccessApp(meData, APP_CODE);
      
      if (!hasAccess) {
        logger.info('[Auth] Access DENIED - clearing token');
        localStorage.removeItem(TOKEN_KEY);
        setError('Access denied. You do not have permission to access INVENTORY.');
        setLoading(false);
        return false;
      }

      // Restore session
      logger.info('[Auth] Session restored successfully, setting auth state');
      setAuthState({
        token,
        user: meData,
        allowedApps: meData.allowed_apps || [],
        isAuthenticated: true
      });
      setLoading(false);
      
      // Background sync on session restore (non-forced, respects cooldown)
      globalSync(false).then(r => {
        logger.info('[Auth] Background sync complete:', r?.status === 'cooldown' ? 'cooldown (skipped)' : r?.results ? Object.keys(r.results).map(k => `${k}: ${r.results[k].status || 'done'}`).join(', ') : 'done');
      }).catch(e => logger.warn('[Auth] Background sync failed:', e.message));
      
      return true;
      
    } catch (err) {
      logger.info('[Auth] /api/inventory/context status: FAILED', err.response?.status || err.message);
      logger.info('[Auth] Clearing invalid token');
      localStorage.removeItem(TOKEN_KEY);
      setAuthState({
        token: null,
        user: null,
        allowedApps: [],
        isAuthenticated: false
      });
      setLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    restoreSession();
  }, [restoreSession]);

  // Login function - returns navigation path on success
  const login = async (email, password) => {
    setError(null);
    logger.info('[Auth] Login - window.location.origin:', window.location.origin);
    
    try {
      logger.info('[Auth] Attempting login for:', email);
      
      // Step 1: Call Control Room login
      const response = await axios.post(`${CONTROL_ROOM_URL}/api/auth/login`, {
        user_email: email,
        password: password,
        app_code: APP_CODE
      });
      
      const data = response.data;
      logger.info('[Auth] Login response received, status: 200');
      
      // Step 2: Store token
      const access_token = data.access_token;
      if (!access_token) {
        throw new Error('No access_token in response');
      }
      
      localStorage.setItem(TOKEN_KEY, access_token);
      logger.info('[Auth] token stored');
      
      // Verify token was stored
      const storedToken = localStorage.getItem(TOKEN_KEY);
      logger.info('[Auth] localStorage.getItem("cr_access_token"):', storedToken ? 'TOKEN_PRESENT' : 'TOKEN_MISSING');
      
      if (!storedToken) {
        throw new Error('Failed to persist token to localStorage');
      }
      
      // Step 3: Call /api/inventory/context with Bearer token
      logger.info('[Auth] Calling /api/inventory/context with Bearer token...');
      const meData = await getMe();
      logger.info('[Auth] /api/inventory/context status: 200 OK');
      logger.info('[Auth] /api/inventory/context full response:', JSON.stringify(meData, null, 2));
      
      // Step 4: Use RBAC helper for access check
      const hasAccess = canAccessApp(meData, APP_CODE);
      
      if (!hasAccess) {
        logger.info('[Auth] Access DENIED - clearing token');
        localStorage.removeItem(TOKEN_KEY);
        throw new Error('Access denied. You do not have permission to access INVENTORY.');
      }
      
      // Step 5: Set auth store
      logger.info('[Auth] Setting auth store: { token, user, allowedApps, isAuthenticated: true }');
      setAuthState({
        token: access_token,
        user: meData,
        allowedApps: meData.allowed_apps || [],
        isAuthenticated: true
      });
      
      // Verify token still present after state update
      const tokenAfterState = localStorage.getItem(TOKEN_KEY);
      logger.info('[Auth] Token after state update:', tokenAfterState ? 'PRESENT' : 'MISSING');
      
      // Background sync on fresh login (forced, ignores cooldown)
      globalSync(true).then(r => {
        logger.info('[Auth] Post-login sync complete:', r?.results ? Object.keys(r.results).map(k => `${k}: ${r.results[k].status || 'done'}`).join(', ') : 'done');
      }).catch(e => logger.warn('[Auth] Post-login sync failed:', e.message));
      
      // Return success with navigation path
      logger.info('[Auth] Login successful, ready to navigate to /');
      return { success: true, navigateTo: '/' };
      
    } catch (err) {
      logger.info('[Auth] Login failed:', err.response?.data?.detail || err.message);
      
      // Only clear token on actual failure
      localStorage.removeItem(TOKEN_KEY);
      setAuthState({
        token: null,
        user: null,
        allowedApps: [],
        isAuthenticated: false
      });
      
      const errorMsg = err.response?.data?.detail || err.message || 'Login failed';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const logout = useCallback(() => {
    logger.info('[Auth] Logging out, clearing state');
    localStorage.removeItem(TOKEN_KEY);
    setAuthState({
      token: null,
      user: null,
      allowedApps: [],
      isAuthenticated: false
    });
    setError(null);
  }, []);

  // Check if authenticated (state OR token presence)
  const checkAuthenticated = useCallback(() => {
    return authState.isAuthenticated || !!localStorage.getItem(TOKEN_KEY);
  }, [authState.isAuthenticated]);

  const contextValue = useMemo(() => ({
    ...authState,
    login, 
    logout, 
    loading, 
    error,
    setError,
    checkAuthenticated,
    canAccessApp,
    tokenKey: TOKEN_KEY
  }), [authState, login, logout, loading, error, checkAuthenticated, canAccessApp]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
